import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { HelperService } from '../../../shared/services/helper.service';
import { ThemeService } from '../../../shared/services/theme.service';
import { TimelineService } from '../../../shared/services/timeline.service';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { CryptoService } from '../../../shared/services/crypto.service';
import { PermissionService } from '../../../shared/services/permissions.service';
import { isCampaignLocked } from '../../../shared/utils/permission-checks';
import { EntityParticipant, EntityParticipantsService } from '../../../shared/services/entity-participants.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../../../shared/components/platform-comments-chat/platform-chat-entity.const';

import {
  CAMPAIGN_WORKSPACE_COMPONENT_NAMES,
  CAMPAIGN_WORKSPACE_ROUTES,
} from '../../../shared/models/campaign-workspace-translations';
import { GeneralUtilsService } from '../../../shared/services/general-utils.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignDriveHelperService {
  constructor(
    private readonly themeService: ThemeService,
    private readonly timelineService: TimelineService,
    private readonly router: Router,
    private readonly projectV2Service: ProjectV2ServiceService,
    private readonly cryptoService: CryptoService,
    private readonly permissionService: PermissionService,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly entityParticipants: EntityParticipantsService,
    private readonly authService: AuthService
  ) {}

  stringToHslColor(initial: string = ''): string | undefined {
    if (!initial) {
      return;
    }

    let hash = 0;
    for (let i = 0; i < initial.length; i++) {
      hash = initial.charCodeAt(i) + ((hash << 5) - hash);
    }

    const h = hash % 360;
    return `hsl(${h}, 75%, 62%)`;
  }

  setProfilePicture(currentUser: any): { initialName: string; nameColor: string; blobPicture: any } | null {
    const fullName = currentUser.fullName
      ? currentUser.fullName
      : `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`;

    const nameArr = fullName.toLowerCase().split(' ');

    if (nameArr.length > 0 && !this.generalUtilsService.isObjectEmpty(nameArr[0][0])) {
      let initials = nameArr[0][0];

      if (nameArr.length > 1 && !this.generalUtilsService.isObjectEmpty(nameArr[1][0])) {
        initials += nameArr[1][0];
      } else {
        initials += nameArr[0][1] || '';
      }

      const nameColor = this.stringToHslColor(initials);
      const blobPicture = currentUser?.BlobPicture ?? currentUser.blobPicture;

      return {
        initialName: initials,
        nameColor,
        blobPicture,
      };
    }

    return null;
  }

  setupContentAndBreadcrumbs(searchPanel: any, isStandAloneSearchEnabled: boolean, workspaceId?: string): void {
    this.themeService.updateContent({
      searchPanel,
      fullWidthContent: true,
      standaloneSearch: isStandAloneSearchEnabled,
    });
  }

  setYearSelected(campaignSearchParams: any, madMonthYear: any, years: string[]): number[] {
    const selectedMonths: string[] = campaignSearchParams?.selectedMonths || [];
    const yearSelected: number[] = [];

    const uniqueYears = selectedMonths
      .map((m) => {
        const year = m.substring(0, 4);
        const month = m.substring(4, 6);

        if (!madMonthYear[year]) {
          madMonthYear[year] = {};
        }
        madMonthYear[year][month] = true;

        return year;
      })
      .filter((value, index, array) => array.indexOf(value) === index);

    // Determine fully selected years
    for (const year of years) {
      const totalMonthSelected = selectedMonths.filter((m) => m.startsWith(year)).length;
      if (totalMonthSelected === 12) {
        yearSelected.push(parseInt(year, 10));
      }
    }

    return yearSelected;
  }

  setDriveCampaignSearchParams(workspaceId: string | null, campaignSearchParams: any, sortBy: string): void {
    if (workspaceId) {
      localStorage.setItem(`campaignSearchParams__${workspaceId}`, JSON.stringify(campaignSearchParams));
      localStorage.setItem(`campaignSortBy__${workspaceId}`, sortBy);
    } else {
      localStorage.setItem('campaignSearchParams', JSON.stringify(campaignSearchParams));
      localStorage.setItem('campaignSortBy', sortBy);
    }
  }

  getDriveCampaignSearchParams(workspaceId: string | null): { campaignSearchParams: any; sortBy: string } {
    let campaignSearchParams: any;
    let sortBy: string;

    if (workspaceId) {
      campaignSearchParams = JSON.parse(localStorage.getItem(`campaignSearchParams__${workspaceId}`) || 'null');
      sortBy = localStorage.getItem(`campaignSortBy__${workspaceId}`) || 'Date';
    } else {
      campaignSearchParams = JSON.parse(localStorage.getItem('campaignSearchParams') || 'null');
      sortBy = localStorage.getItem('campaignSortBy') || 'Date';
    }

    return { campaignSearchParams, sortBy };
  }

  redirectToDashboard(drive: any): void {
    this.timelineService.componentName.next({
      componentName: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE_COMPONENT,
      bannerUrl: drive.banner,
    });

    this.timelineService.lastSelectedDriveCampaign = drive;
    this.projectV2Service.driveCoordinatorMilestone = undefined;

    const path = CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE_DASHBOARD;
    this.router
      .navigate([path], {
        queryParams: {
          wid: this.cryptoService.encryptString(drive.workspaceId),
          dc: this.cryptoService.encryptString(drive.id),
        },
      })
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));

    this.timelineService.updateMilestone({});
  }

  gotoWorkspace(project: any, isWorkspaceEnabled: boolean): void {
    if (!isWorkspaceEnabled) {
      return;
    }

    this.router.navigateByUrl(CAMPAIGN_WORKSPACE_ROUTES.TEMPORARY_REDIRECT, { skipLocationChange: true }).then(() => {
      this.router
        .navigate([CAMPAIGN_WORKSPACE_ROUTES.CAMPAIGN_DRIVE], {
          queryParams: {
            wid: this.cryptoService.encryptString(project?.workspaceId),
          },
        })
        .then(() => {})
        .catch((err) => console.error('Navigation error:', err));
    });
  }

  setCampaignPermissions(driveCampaigns: any[], authUser: any): any[] {
    return driveCampaigns.map((d) => {
      d.locked = isCampaignLocked(this.permissionService, d, authUser.username);
      return d;
    });
  }

  // The role-filtered brand-users endpoints are retired — both picker lists come from the
  // unified per-entity participants endpoint (same as the task surfaces). Without a
  // workspace (standalone/all-workspaces search), fall back to the brand's participants,
  // matching the legacy brand-scoped behavior.
  getUsers(workspaceId: string): Observable<{ productDevManagers: any[]; cncManagers: any[] }> {
    if (workspaceId) {
      return this.entityParticipants
        .getParticipants(PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE, workspaceId)
        .pipe(map((participants: EntityParticipant[]) => this.toManagerLists(participants)));
    }
    const brandId: string | undefined = this.authService.getUserBrand()?.id;
    if (!brandId) {
      return of({ productDevManagers: [], cncManagers: [] });
    }
    return this.entityParticipants
      .getParticipants(PLATFORM_CHAT_ENTITY_TYPE.BRAND, brandId)
      .pipe(map((participants: EntityParticipant[]) => this.toManagerLists(participants)));
  }

  // Separate copies: the caller assigns both lists to component state where per-list
  // mutation would otherwise alias.
  private toManagerLists(participants: EntityParticipant[]): { productDevManagers: any[]; cncManagers: any[] } {
    return { productDevManagers: [...participants], cncManagers: [...participants] };
  }
}
